import React, { useState, useMemo, useCallback } from 'react';
import { 
  SignatureBlock, 
  SignatureDynamicData, 
  createTextBlock, 
  createImageBlock, 
  createContactRowBlock, 
  createDividerBlock, 
  createSpacerBlock, 
  createSocialIconsBlock,
  createId
} from './blocks/types';
import { 
  SignatureGridData, 
  GridCell,
  createCell,
  createDefaultGridData,
  splitCell as splitCellHelper,
  updateCellInGrid,
  deleteCellFromGrid,
  findCellById,
  migrateToGridData
} from './grid/types';
import { signatureTemplates } from './SignatureTemplates';
import { gridToHtml, gridDataToJson, jsonToGridData } from './utils/signatureToHtml';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Plus, Eye, LayoutTemplate } from 'lucide-react';
import { GridCellComponent } from './grid/GridCell';
import { BlockType } from './grid/AddContentPopover';
import { ImageBlockEditor } from './blocks/ImageBlock';
import { TextBlockEditor } from './blocks/TextBlock';
import { ContactRowBlockEditor } from './blocks/ContactRowBlock';
import { SocialIconsBlockEditor } from './blocks/SocialIconsBlock';
import { DividerBlockEditor } from './blocks/DividerBlock';
import { SpacerBlockEditor } from './blocks/SpacerBlock';

interface SignatureBuilderProps {
  value: string;
  onChange: (value: string) => void;
  userProfile: SignatureDynamicData['user'];
  company?: SignatureDynamicData['company'];
  disabled?: boolean;
}

const SignatureBuilder: React.FC<SignatureBuilderProps> = ({
  value,
  onChange,
  userProfile,
  company,
  disabled = false,
}) => {
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  // Parse value as JSON signature grid data, or initialize empty
  const gridData = useMemo((): SignatureGridData => {
    if (!value) return createDefaultGridData();
    const parsed = jsonToGridData(value);
    if (parsed) return parsed;
    // Try legacy format migration
    try {
      const legacyParsed = JSON.parse(value);
      if (legacyParsed && Array.isArray(legacyParsed.blocks)) {
        return migrateToGridData(legacyParsed);
      }
    } catch {}
    return createDefaultGridData();
  }, [value]);

  const dynamicData: SignatureDynamicData = { user: userProfile, company };

  const updateGrid = useCallback((newData: SignatureGridData) => {
    onChange(gridDataToJson(newData));
  }, [onChange]);

  // Find selected cell
  const selectedCell = useMemo(() => {
    if (!selectedCellId) return null;
    return findCellById(gridData.rootCells, selectedCellId);
  }, [selectedCellId, gridData.rootCells]);

  // Add content to a cell
  const handleAddContent = useCallback((cellId: string, blockType: BlockType) => {
    let newBlock: SignatureBlock;
    switch (blockType) {
      case 'text': newBlock = createTextBlock(); break;
      case 'image': newBlock = createImageBlock(); break;
      case 'contact': newBlock = createContactRowBlock(); break;
      case 'social': newBlock = createSocialIconsBlock(); break;
      case 'divider': newBlock = createDividerBlock(); break;
      case 'spacer': newBlock = createSpacerBlock(); break;
      default: return;
    }
    
    const newRootCells = updateCellInGrid(gridData.rootCells, cellId, (cell) => ({
      ...cell,
      content: newBlock,
    }));
    
    updateGrid({ ...gridData, rootCells: newRootCells });
    setSelectedCellId(cellId);
  }, [gridData, updateGrid]);

  // Split a cell
  const handleSplit = useCallback((cellId: string, direction: 'horizontal' | 'vertical', widths: string[]) => {
    const newRootCells = updateCellInGrid(gridData.rootCells, cellId, (cell) => 
      splitCellHelper(cell, direction, widths)
    );
    updateGrid({ ...gridData, rootCells: newRootCells });
  }, [gridData, updateGrid]);

  // Delete a cell
  const handleDelete = useCallback((cellId: string) => {
    // Don't delete the last root cell
    if (gridData.rootCells.length === 1 && gridData.rootCells[0].id === cellId) {
      // Reset to empty cell instead
      updateGrid({ ...gridData, rootCells: [createCell(0)] });
      setSelectedCellId(null);
      return;
    }
    
    const newRootCells = deleteCellFromGrid(gridData.rootCells, cellId);
    updateGrid({ ...gridData, rootCells: newRootCells.length > 0 ? newRootCells : [createCell(0)] });
    if (selectedCellId === cellId) setSelectedCellId(null);
  }, [gridData, updateGrid, selectedCellId]);

  // Update block in selected cell
  const handleUpdateBlock = useCallback((updatedBlock: SignatureBlock) => {
    if (!selectedCellId) return;
    const newRootCells = updateCellInGrid(gridData.rootCells, selectedCellId, (cell) => ({
      ...cell,
      content: updatedBlock,
    }));
    updateGrid({ ...gridData, rootCells: newRootCells });
  }, [selectedCellId, gridData, updateGrid]);

  // Change block type
  const handleChangeBlockType = useCallback((newType: BlockType) => {
    if (!selectedCellId) return;
    
    let newBlock: SignatureBlock;
    switch (newType) {
      case 'text': newBlock = createTextBlock(); break;
      case 'image': newBlock = createImageBlock(); break;
      case 'contact': newBlock = createContactRowBlock(); break;
      case 'social': newBlock = createSocialIconsBlock(); break;
      case 'divider': newBlock = createDividerBlock(); break;
      case 'spacer': newBlock = createSpacerBlock(); break;
      default: return;
    }
    
    const newRootCells = updateCellInGrid(gridData.rootCells, selectedCellId, (cell) => ({
      ...cell,
      content: newBlock,
    }));
    updateGrid({ ...gridData, rootCells: newRootCells });
  }, [selectedCellId, gridData, updateGrid]);

  // Add new row
  const handleAddRow = useCallback(() => {
    const newCell = createCell(0);
    updateGrid({ ...gridData, rootCells: [...gridData.rootCells, newCell] });
    setSelectedCellId(newCell.id);
  }, [gridData, updateGrid]);

  // Apply template
  const applyTemplate = useCallback((templateId: string) => {
    const template = signatureTemplates.find(t => t.id === templateId);
    if (template) {
      // Templates now return SignatureGridData directly
      const templateData = template.data as unknown as SignatureGridData;
      
      // Clone with new IDs
      const cloneCell = (cell: GridCell, depth: number): GridCell => {
        const newCell: GridCell = {
          ...cell,
          id: createId(),
          depth,
          content: cell.content ? { ...cell.content, id: createId() } : null,
          children: cell.children ? cell.children.map(c => cloneCell(c, depth + 1)) : null,
        };
        return newCell;
      };
      
      const clonedRootCells = templateData.rootCells.map(c => cloneCell(c, 0));
      
      updateGrid({
        ...templateData,
        rootCells: clonedRootCells,
      });
      setShowTemplates(false);
      setSelectedCellId(null);
    }
  }, [updateGrid]);

  // Render block-specific editor
  const renderBlockSpecificEditor = (block: SignatureBlock) => {
    switch (block.type) {
      case 'image':
        return <ImageBlockEditor block={block} onChange={handleUpdateBlock} />;
      case 'text':
        return <TextBlockEditor block={block} onChange={handleUpdateBlock} />;
      case 'contact':
        return <ContactRowBlockEditor block={block} onChange={handleUpdateBlock} />;
      case 'social':
        return <SocialIconsBlockEditor block={block} onChange={handleUpdateBlock} />;
      case 'divider':
        return <DividerBlockEditor block={block} onChange={handleUpdateBlock} />;
      case 'spacer':
        return <SpacerBlockEditor block={block} onChange={handleUpdateBlock} />;
      default:
        return null;
    }
  };

  // Render block editor with type selector
  const renderBlockEditor = () => {
    if (!selectedCell?.content) {
      return (
        <div className="text-sm text-muted-foreground text-center py-8">
          Select a cell with content to edit
        </div>
      );
    }
    
    const block = selectedCell.content;
    
    return (
      <div className="space-y-4">
        {/* Block Type Selector */}
        <div className="space-y-2">
          <Label className="text-xs">Block Type</Label>
          <Select 
            value={block.type} 
            onValueChange={(value) => handleChangeBlockType(value as BlockType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="image">Image/Logo</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="contact">Contact Row</SelectItem>
              <SelectItem value="social">Social Icons</SelectItem>
              <SelectItem value="divider">Divider</SelectItem>
              <SelectItem value="spacer">Spacer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Separator />
        
        {/* Block-specific editor */}
        {renderBlockSpecificEditor(block)}
      </div>
    );
  };

  // Use absolute URLs for preview so it matches email output (WYSIWYG)
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const previewHtml = useMemo(() => gridToHtml(gridData, dynamicData, { baseUrl }), [gridData, dynamicData, baseUrl]);

  const hasContent = gridData.rootCells.some(cell => 
    cell.content !== null || (cell.children && cell.children.length > 0)
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTemplates(!showTemplates)}
          disabled={disabled}
        >
          <LayoutTemplate className="h-4 w-4 mr-1" />
          {showTemplates ? 'Hide Templates' : 'Templates'}
        </Button>
      </div>

      {/* Templates */}
      {showTemplates && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {signatureTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => applyTemplate(template.id)}
              className="p-3 border rounded-lg hover:border-primary transition-colors text-left"
            >
              <div className="h-12 rounded mb-2" style={{ background: template.thumbnail }} />
              <p className="font-medium text-sm">{template.name}</p>
              <p className="text-xs text-muted-foreground">{template.description}</p>
            </button>
          ))}
        </div>
      )}

      {/* Builder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Grid Canvas */}
        <Card className="p-4 min-h-[250px]">
          <p className="text-xs text-muted-foreground mb-3">
            Click cells to add content • Split cells for columns/rows
          </p>
          
          <div className="space-y-2">
            {gridData.rootCells.map((cell) => (
              <GridCellComponent
                key={cell.id}
                cell={cell}
                dynamicData={dynamicData}
                selectedCellId={selectedCellId}
                onSelect={setSelectedCellId}
                onAddContent={handleAddContent}
                onSplit={handleSplit}
                onDelete={handleDelete}
                isRoot={gridData.rootCells.length === 1}
              />
            ))}
          </div>
          
          {/* Add Row Button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-3 border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
            onClick={handleAddRow}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>
        </Card>

        {/* Editor Panel */}
        <Card className="p-4">
          <p className="text-xs font-medium mb-3">Block Settings</p>
          <ScrollArea className="h-[300px]">
            {renderBlockEditor()}
          </ScrollArea>
        </Card>
      </div>

      {/* Preview */}
      {hasContent && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Eye className="h-4 w-4" /> Email Preview
          </div>
          <Card className="p-4 bg-muted/20">
            <div className="border-t pt-3 mt-3" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </Card>
        </div>
      )}
    </div>
  );
};

export default SignatureBuilder;
